import { differenceInMinutes } from "date-fns";
import { waitForMs } from "../await";
import { getDelay } from "./delay";

const MAX_WAITING_TIME_MINUTES = (import.meta.env.FOOJI_MAX_WAITING_TIME_MINUTES as number) || 3;

export type PollingStatus = boolean | undefined;

type PollerHandlers = {
  pollingFunction: () => Promise<PollingStatus>;
  onPollingSuccess?: (result: PollingStatus) => void | Promise<void>;
  timeOutHandler?: () => void | Promise<void>;
};

type PollerOptions = {
  delay?: number;
  maxTime?: number;
  allowInfinitePolling?: boolean;
  keepPollingOnError?: boolean;
  useExponentialBackoff?: boolean;
};

type PollerConfig = PollerHandlers & {
  waitStart: Date;
  pollerObject: { stopPolling: boolean };
  delay: number;
  maxTime: number;
  allowInfinitePolling: boolean;
  keepPollingOnError: boolean;
  attempt: number;
  useExponentialBackoff?: boolean;
};

export const startPoller = (
  waitStart: Date,
  handlers: PollerHandlers,
  options?: PollerOptions
): { stopPolling: boolean } => {
  const pollerObject = { stopPolling: false };
  const delay = getDelay(options?.delay);
  const maxTime = options?.maxTime ?? MAX_WAITING_TIME_MINUTES;
  const allowInfinitePolling = options?.allowInfinitePolling ?? false;
  const keepPollingOnError = options?.keepPollingOnError ?? true;

  const config: PollerConfig = {
    waitStart,
    pollerObject,
    delay,
    maxTime,
    allowInfinitePolling,
    keepPollingOnError,
    ...handlers,
    attempt: 0,
    useExponentialBackoff: options?.useExponentialBackoff ?? true
  };

  void (async () => await Poller.start(config))();
  return pollerObject;
};

export const Poller = {
  start: async ({
    waitStart,
    pollerObject,
    pollingFunction,
    delay,
    maxTime,
    allowInfinitePolling,
    keepPollingOnError,
    onPollingSuccess,
    timeOutHandler,
    attempt,
    useExponentialBackoff
  }: PollerConfig): Promise<void> => {
    const elapsedTime = differenceInMinutes(new Date(), waitStart);

    const pollerConfig = {
      waitStart,
      pollerObject,
      pollingFunction,
      delay,
      maxTime,
      allowInfinitePolling,
      keepPollingOnError,
      onPollingSuccess,
      timeOutHandler,
      attempt,
      useExponentialBackoff
    };

    let pollingStatus: PollingStatus = false;

    if (pollerObject.stopPolling) {
      return;
    }

    if (!allowInfinitePolling && elapsedTime >= maxTime) {
      if (timeOutHandler) {
        await timeOutHandler();
      }
      return;
    }

    const waitDelay = useExponentialBackoff? delay * Math.pow(2, pollerConfig.attempt++) : delay;
    console.log("Poller.start: ", { waitStart, delay, maxTime, attempt, waitDelay, useExponentialBackoff });
    await waitForMs(waitDelay);

    try {
      pollingStatus = await pollingFunction();
      if (pollingStatus) {
        return onPollingSuccess && onPollingSuccess(pollingStatus);
      }
    } catch (error) {
      if (!keepPollingOnError) {
        return;
      }
    }

    await Poller.start(pollerConfig);
  }
};
